import React, {useState, useEffect, useContext, useRef, useCallback} from 'react';
import {SipClient, CallManager} from '@dmc/sip-client';
import {RTCAudio} from '@dmc/react-rtc-media-components';
import CallControls from './CallControls'
import VideoRoom from './VideoRoom'
import Snake from 'react-simple-snake'
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import ConfigContext from '../contexts/configContext'
import CrossfadeImage from 'react-crossfade-image'
import DefaultPoster from '../images/default-video-poster.png'
import GetLiveCallPath from './LiveCallPath';
import Avatar, {randomColor} from './Avatar';
import { Button } from '@mui/material';
import { getDummyVideoTrack } from '../helpers/StreamHelpers';

export default function Call(
    {
      cameraAllowed,
      hasDummyVideoTrack,
      meetingId,
      pin,
      extension,
      callerName,
      extraHeaders,
      localStream,
      audioDevices,
      videoDevices,
      localAudioInputId,
      localVideoInputId,
      conferenceSettings,
      onCallEnd,
      onAudioInputChange,
      onVideoInputChange,
      setHasDummyVideoTrack,
    }
  ) {
    const config = useContext(ConfigContext)
    const [callState, setCallState] = useState('Initialising...')
    const [sipClient, setSipClient] = useState(null)
    const [session, setSession] = useState(null)
    const [screensharingAllowed, setScreenSharingAllowed] = useState(true)
    const [screensharingEnabled, setScreenSharingEnabled] = useState(false)
    const [audioStreams, setAudioStreams] = useState([])
    const [videoStreams, setVideoStreams] = useState([])
    const [answered, setAnswered] = useState(false)
    const [muted, setMuted] = useState(false)
    const [videomuted, setVideomuted] = useState(hasDummyVideoTrack ? true : false)
    const [dtmfEnabled, setDtmfEnabled] = useState(true)
    const [localVideoStream, setLocalVideoStream] = useState(localStream)
    const [fullscreen, setFullscreen] = useState(false)
    const [onHoldMode, setOnHoldMode] = useState("images")
    const [onHoldImages, setOnHoldImages] = useState([GetLiveCallPath(DefaultPoster)])
    const [onHoldVideos, setOnHoldVideos] = useState([])
    const [onHoldVideoIndex, setOnHoldVideoIndex] = useState(0)
    const [onHoldImageIndex, setOnHoldImageIndex] = useState(0)
    const [onHoldImageTransitionDuration, setOnHoldImageTransitionDuration] = useState(2000)
    const [onHoldImageTransitionInterval, setOnHoldImageTransitionInterval] = useState(10000)
    const [confbridgeChannels, setConfbridgeChannels] = useState([])
    const [confbridgeChannelAvatars, setConfbridgeChannelAvatars] = useState(new Map())
    const [confbridgeTalkingStatus, setConfbridgeTalkingStatus] = useState(new Map())
    const [confbridgeAdmin, setConfbridgeAdmin] = useState(false)
    const [confbridgeModerator, setConfbridgeModerator] = useState(false)
    const [localConfbridgeChannelId, setLocalConfbridgeChannelId] = useState(null)
    const [msidChannelIdMap, setMsidChannelIdMap] = useState(new Map())
    const [notifications, setNotifications] = useState(new Map())
    const [drawerContent, setDrawerContent] = useState(null)
    const [dtmfActions, setDtmfActions] = useState([])
    const [chatMessages, setChatMessages] = useState([])
    const [unreadChatMessages, setUnreadChatMessages] = useState(false)
    const fullscreenHandle = useFullScreenHandle();
    const onHoldVideoRef = useRef();

    useEffect(() => {
      if (config?.call?.dtmfActions) setDtmfActions(config.call.dtmfActions)
      return () => {
        cleanup()
      }
    }, [])

    useEffect(() => {
      if (confbridgeChannels.length < 1) return
      confbridgeChannels.forEach(channel => {
        let existing = confbridgeChannelAvatars.get(channel.id)
        if (!existing) {
          let newAvatar = <Avatar name={channel.caller.name} bgColor={randomColor()}/>
          setConfbridgeChannelAvatars(new Map(confbridgeChannelAvatars.set(channel.id, newAvatar)))
        }
      })

      var autoMuteTimeout = null
      if (confbridgeChannels.length === 1) {
        autoMuteTimeout = setTimeout(() => {
            handleAutoMute()
        }, 20000)
      }
      return () => clearInterval(autoMuteTimeout)
    }, [confbridgeChannels, confbridgeChannelAvatars])

    useEffect(() =>{
        if (!config) return 

        if (Object.hasOwn(config?.devices, 'screensharingEnabled')) setScreenSharingAllowed(config.devices.screensharingEnabled)
        if (Object.hasOwn(config?.callControls, 'dtmfEnabled')) setDtmfEnabled(config.callControls.dtmfEnabled)
        if (config?.call?.onHold?.mode) setOnHoldMode(config.call.onHold.mode)
        if (config?.call?.onHold?.images) setOnHoldImages(config.call.onHold.images)
        if (config?.call?.onHold?.videos) setOnHoldVideos(config.call.onHold.videos)
        if (config?.call?.onHold?.imageTransitionDuration) setOnHoldImageTransitionDuration(config.call.onHold.imageTransitionDuration)
        if (config?.call?.onHold?.imageTransitionInterval) setOnHoldImageTransitionInterval(config.call.onHold.imageTransitionInterval)

        const callManager = new CallManager();
        const pcConfig = {}

        if (config?.call?.iceServers) {
          console.log('iceServers:', config.call.iceServers)
          pcConfig['iceServers'] = config.call.iceServers
        }

        const options = {
          name: callerName,
          sipUri: config?.call?.sipUri ? 
                    config.call.sipUri 
                  :
                    'dmc-live-call@localhost',
          wsUri: config?.call?.wsUri ? 
                    config.call.wsUri 
                  :
                    'wss://hv04-andy-dev01.dmcip.com:8089/ws',
          pcConfig: pcConfig,
          callManager: callManager,
        }


        setSipClient(new SipClient(options))

        document.addEventListener("fullscreenchange", () => {
          if (document.fullscreenElement) {
            setFullscreen(true)
          } else {
            setFullscreen(false)
          }
        });
    }, [config, localStream])

    useEffect(() => {
      if (!sipClient) return
      sipClient.start();

      sipClient.on('connected', () => {
        const video = true;
        setCallState('Dialling in');
        extraHeaders.push('X-DMC-Live-Call-Client: true')
        if (meetingId)
          extraHeaders.push(`X-DMCConferenceID: ${meetingId}`)
        if (pin)
          extraHeaders.push(`X-DMCConferencePIN: ${pin}`)
        sipClient.call(extension, video, extraHeaders, localStream)
      })

      sipClient.on('disconnected', () => {
        cleanup()
      })

      sipClient.on('session', (session) => {
        sipClient._callManager.handleSession(session);
      })

      sipClient.on('confbridgeWelcome', handleConfbridgeWelcome)
      sipClient.on('confbridgeJoin', handleConfbridgeJoin)
      sipClient.on('confbridgeLeave', handleConfbridgeLeave)
      sipClient.on('confbridgeTalking', handleConfbridgeTalking)
      sipClient.on('asteriskChannelInfo', handleAsteriskChannelInfo)

      sipClient._callManager.on('activeSession', (sipsession) => {
        setCallState('Call in progress');
        setSession(sipsession)
      });

      return () => {
        if (!sipClient) return
        sipClient.removeAllListeners()
      }
    }, [sipClient])

    const handleNotificationDelete = useCallback((id) => {
      let newMap = new Map(notifications)
      newMap.delete(id)
      setNotifications(newMap)
    }, [notifications])

    const handleAutoMute = () => {
      console.log('automute')
      session.mute()
      setMuted(true)
      session.videomute()
      setVideomuted(true)
      let msg = `You have been muted. You're the only person in this conference.` 
      let notification = { severity: 'warning', sticky: true, msg: msg}
      setNotifications(new Map(notifications.set('automute', notification)))
    }

    useEffect(() => {
      if (!sipClient) return
      if (!localConfbridgeChannelId) return

      const handleNudgeReceived = (evt) => {
        if (evt.To !== localConfbridgeChannelId) return // not for us
        let channel = confbridgeChannels.find(c => c.id === evt.From)
        if (!channel) return

        let msg = `You have been nudged by ${channel.caller.name}!` 
        let notification = { severity: 'warning', sticky: false, msg: msg}
        setNotifications(new Map(notifications.set(evt.Id, notification)))
      }
      const handleChatMessage = (msg) => {
        let newMsg = msg
        let channel = confbridgeChannels.find(c => c.id === msg.From)
        let avatar = confbridgeChannelAvatars.get(msg.From)
        let timestamp = new Date()
        newMsg['LocalUser'] = false
        newMsg['Channel'] = channel
        newMsg['Avatar'] = avatar
        newMsg['Timestamp'] = timestamp
        setChatMessages(prevArray => [...prevArray, newMsg])
        if (drawerContent !== 'Conversation') { 
          setUnreadChatMessages(true)
          let action = <Button 
            color="inherit"
            size="small"
            onClick={() => {
              setDrawerContent('Conversation')
              handleNotificationDelete(msg.Id)
            }}
          >
            Read
          </Button>
          let msg = `New message received from ${channel.caller.name}` 
          let notification = { severity: 'info', sticky: false, msg: msg, action: action}
          setNotifications(new Map(notifications.set(msg.Id, notification)))
        }
      }

      // Check if we are an admin
      const localConfbridgeChannel = confbridgeChannels.find(channel => channel.id === localConfbridgeChannelId)
      if (localConfbridgeChannel) {
        setConfbridgeAdmin(localConfbridgeChannel.admin)
        if (localConfbridgeChannel.caller?.name === "MODERATOR") setConfbridgeModerator(true)
      }

      sipClient.on('nudgeReceived', handleNudgeReceived)
      sipClient.on('chatMessage', handleChatMessage)

      return () => {
        sipClient.removeAllListeners('nudgeReceived')
        sipClient.removeAllListeners('chatMessage')
      }

    }, [sipClient, localConfbridgeChannelId, confbridgeChannels, drawerContent, confbridgeChannelAvatars, notifications, handleNotificationDelete])

    useEffect(() => {
        let timeout = null
        if (onHoldMode === 'images' && onHoldImages.length > 0) {
          setTimeout(() => {
            loadNextImage() 
          }, onHoldImageTransitionInterval);
        }

        return () => {
          if (timeout) clearTimeout(timeout)
        }
    }, [onHoldMode, onHoldImages, onHoldImageTransitionInterval])
          
    useEffect(() => {
        if (!session) return
        
        session.on('answer', (evt) => {
          setAnswered(true)
          console.log('answered now!')
        })

        // Set up event handlers
        session.on('msidLabelsUpdate', handleMsidLabelsUpdate)
        session.on('addTrack', (evt) => {
            console.log('New track added', evt);
            if (evt.track.kind === 'audio') {
                audioStreams.push(evt.streams[0])
                setAudioStreams([...audioStreams])
            } else if (evt.track.kind === 'video') {
              /*  Because asterisk can send dummy video tracks that have no data
                we have to workaround this by creating a dummy video element
                and waiting until it has loaded metadata. It will only do this
                if there is actual data, and the dummy has none. */
                const dummyvideo = document.createElement("video");
                let newStream = evt.streams[0]
                dummyvideo.srcObject = newStream;
                dummyvideo.onloadedmetadata = () => {
                  if (dummyvideo.videoHeight < 10 && dummyvideo.videoWidth < 10) return;
                  console.log("Real video track detected, adding to streams");
                  console.log('track constraints:', newStream.getVideoTracks()[0].getConstraints())
                  setVideoStreams(prevStreams => [...prevStreams, newStream])
                };

                evt.track.addEventListener('ended', () => {
                  console.log(`Stream ${newStream.id} has ended, removing`);
                  setVideoStreams(prevStreams => {
                    return prevStreams.filter(stream => {
                      return stream.id !== newStream.id;
                    });
                  })
                })
            }
        })

        session.on('terminate', (evt) => {
          onCallEnd()
        })

    }, [session])

    useEffect(() => {
      if (!session) return
      const handleChatSent = (msg) => {
        let newMsg = msg
        let channel = confbridgeChannels.find(c => c.id === msg.From)
        let avatar = confbridgeChannelAvatars.get(msg.From)
        let timestamp = new Date()
        newMsg['LocalUser'] = true
        newMsg['Channel'] = channel
        newMsg['Avatar'] = avatar
        newMsg['Timestamp'] = timestamp
        setChatMessages(prevArray => [...prevArray, newMsg])
      }
      session.on('chatSent', handleChatSent)
      return () => session.removeAllListeners('chatSent')
    }, [session, chatMessages, localConfbridgeChannelId, confbridgeChannels, confbridgeChannelAvatars])

    useEffect(() => {
      // if the video changes we need to trigger the video element to reload
      onHoldVideoRef.current?.load()
    }, [onHoldVideoIndex])

    useEffect(() => {
      return () => {
        //make sure all streams get closed off properly
        if (localStream) {
          console.log('stopping all localStream tracks')
          localStream.getTracks().forEach(track => track.stop())
        }
        if (localVideoStream) {
          console.log('stopping all localVideoStream tracks')
          localVideoStream.getTracks().forEach(track => track.stop())
        }
      } 
    }, [])

    useEffect(() => {
      return () => {
        if (localVideoStream) {
          //stop all tracks when secondary stream changed
          console.log('stopping all localVideoStream video tracks')
          localVideoStream.getVideoTracks().forEach(track => track.stop())
        }
      } 
    }, [localVideoStream])

    useEffect(() => {
      return () => {
        if (localStream) {
          console.log('stopping all localStream tracks')
          localStream.getTracks().forEach(track => track.stop())
        }
      } 
    }, [localStream])


    useEffect(() => {
      let timeout = setTimeout(() => {
       loadNextImage() 
      }, onHoldImageTransitionInterval);

      return () => {
        clearTimeout(timeout)
      }
    }, [onHoldImageIndex, onHoldImageTransitionInterval])

  const cleanup = () => {
    if (!sipClient) return
    sipClient.stop()
    sipClient.removeAllListeners('connected')
    sipClient.removeAllListeners('session')
  }

  const handleConfbridgeWelcome = (evt) => {
    setConfbridgeChannels(evt.channels)
  }
  
  const handleConfbridgeJoin = (evt) => {
    setConfbridgeChannels(prevArray => [...prevArray, ...evt.channels])
  }

  const handleConfbridgeLeave = (evt) => {
    let channelLeaving = evt.channels[0]
    setConfbridgeChannels(prevArray => prevArray.filter(channel => channel.id !== channelLeaving.id))
  }

  const handleConfbridgeTalking = (evt) => {
    setConfbridgeTalkingStatus(new Map(confbridgeTalkingStatus.set(evt.channels[0].id, evt.channels[0].talking_status === 'on')))
  }

  const handleAsteriskChannelInfo = (evt) => {
    setLocalConfbridgeChannelId(evt.id)
  }

  const handleMsidLabelsUpdate = (map) => {
    setMsidChannelIdMap(map)
  }

  const renderOnHold = () => {
    let inner = null

    switch(onHoldMode) {
      case "images":
        inner = renderOnHoldImages();
          break;
      case "videos":
        inner = renderOnHoldVideos();
        break;
      case "games":
        inner = renderOnHoldGames();
        break;
      default:
        inner = renderOnHoldImages();
    }
    
    let component  = 
      <div className='live-call-call-on-hold'>
        {inner}
      </div>
    return component;
  }

  const renderOnHoldImages = () => {
    // let src = onHoldImages[Math.floor(Math.random()*onHoldImages.length)];
    let component  = 
        <CrossfadeImage
         style={{
          width: '100%',
          height: '100%',
          objectFit: "cover",
         }}
         src={onHoldImages[onHoldImageIndex]} 
         containerClass='live-call-call-image'
         duration={onHoldImageTransitionDuration}
        />
    return component;
  }

  const renderOnHoldVideos = () => {
    let component  = 
      <div className='live-call-call-video'>
        <video
         ref={onHoldVideoRef}
         style={{
          width: '100%',
          height: '100%',
          objectFit: "cover",
         }}
         src={onHoldVideos[onHoldVideoIndex]}
         autoPlay
         onEnded={() => loadNextVideo()}
        >
          video not supported
        </video>
      </div>
    return component;
  }

  const renderOnHoldGames = () => {
    let component  = 
      <div className='live-call-call-game'>
        <Snake
          percentageWidth='60'
          snakeColor='#46BF8C'
        />
      </div>
    return component;
  }

  const renderRemoteVideo = () => {
    let component  = <VideoRoom 
      streams={videoStreams}
      confbridgeChannels={confbridgeChannels}
      confbridgeChannelAvatars={confbridgeChannelAvatars}
      confbridgeTalkingStatus={confbridgeTalkingStatus}
      confbridgeAdmin={confbridgeAdmin}
      confbridgeModerator={confbridgeModerator}
      msidChannelIdMap={msidChannelIdMap}
      dtmfActions={dtmfActions}
      chatMessages={chatMessages}
      drawerContent={drawerContent}
      setDrawerContent={setDrawerContent}
      notifications={notifications}
      localVideoStream={localVideoStream}
      screensharing={screensharingEnabled}
      cameraAllowed={cameraAllowed}
      audioDevices={audioDevices}
      videoDevices={videoDevices}
      selectedAudioDeviceId={localAudioInputId}
      selectedVideoDeviceId={localVideoInputId}
      onNotificationDelete={handleNotificationDelete}
      onNudgeSend={handleNudgeSend}
      onKickSend={handleKickSend}
      onDtmfKey={(key) => {
        console.log(`DTMF Key pressed`, key)
        session.sendDtmf(key)
      }}
      onAudioChange={(deviceId) => handleDeviceChange('audio', deviceId)}
      onVideoChange={(deviceId) => handleDeviceChange('video', deviceId)}
      onSendChatMessage={(msg) => {
        session.sendChat(
          localConfbridgeChannelId,
          msg,
          null
        )
      }}
      />
    return component;
  }

  const loadNextVideo = () => {
    let current = onHoldVideoIndex
    let next = current + 1
    if (next === onHoldVideos.length) { 
      next = 0
    }
    setOnHoldVideoIndex(next) 
  }

  const loadNextImage = () => {
    let current = onHoldImageIndex
    let next = current + 1
    if (next === onHoldImages.length) { 
      next = 0
    }
    setOnHoldImageIndex(next) 
  }

  const getCameraVideo = (useDummyVideo, startMuted) => {
    if (useDummyVideo) {
        //replace the screenshare with a dummy track rather than starting the webcam
        let stream = new MediaStream([getDummyVideoTrack()])
        replaceTrack(stream, startMuted)
    } else {
      //get webcam video track
      const constraints = {
        video: { 
          deviceId: localVideoInputId ? { exact: localVideoInputId} : undefined,
        },
      };
      if (config?.call?.videoConstraints) {
        constraints['video'] = { ...constraints['video'], ...config.call.videoConstraints }
      }
      
      navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => replaceTrack(stream, startMuted))
      .catch(handleError);
    }
  }

  const handleScreenshare = (state) => {

    if (state) {
      //get new screenshare video track

      const constraints = {
        video: { cursor: "always" },
      };
      if (config?.call?.screenshareConstraints) {
        constraints['video'] = { ...constraints['video'], ...config.call.screenshareConstraints }
      }

      navigator.mediaDevices
      .getDisplayMedia(constraints)
      .then(replaceTrack)
      .then((tracks) => {
        setScreenSharingEnabled(true) 
        tracks[1].addEventListener("ended", () => {
          setScreenSharingEnabled(false)
          handleScreenshare(false)
        });
      })
      .catch(handleError);

    } else {
      if (hasDummyVideoTrack) {
        getCameraVideo(true, videomuted)
      } else {
        getCameraVideo(false, videomuted)
      }
      setScreenSharingEnabled(false)
    }
  }

  const replaceTrack = (newstream, startVideoMuted) => {
    console.log('new secondary stream', newstream)
    const newAudioTrack = newstream.getAudioTracks()?.[0]
    const newVideoTrack = newstream.getVideoTracks()?.[0]
    if (newAudioTrack) {
      let audioSender = session._rtcSession._connection.getSenders().find(function (s) {
        return s.track.kind === newAudioTrack.kind;
      });
      if (audioSender){
        localStream.getAudioTracks().forEach(track => track.stop())
        audioSender.replaceTrack(newAudioTrack)
      }
    }
    if (newVideoTrack) {
      if (startVideoMuted) newVideoTrack.enabled = false
      setLocalVideoStream(newstream)
      let videoSender = session._rtcSession._connection.getSenders().find(function (s) {
        return s.track?.kind === newVideoTrack.kind;
      });
      if (videoSender){
        localStream.getVideoTracks().forEach(track => track.stop())
        videoSender.replaceTrack(newVideoTrack)
      }
    }
    return [newAudioTrack, newVideoTrack]
  }

  const handleDeviceChange = (deviceType, deviceId) => {
    console.log(`handleDeviceChange - ${deviceType} - ${deviceId}`)

    if(deviceType === 'audio') onAudioInputChange(deviceId)
    if(deviceType === 'video') onVideoInputChange(deviceId)
    if(deviceType === 'video' && screensharingEnabled) return

    let constraints = {}
    constraints[deviceType] = 
      { 
        deviceId: deviceId ? { exact: deviceId} : undefined,
      }
    if (deviceType === 'video' && config?.call?.videoConstraints) {
      constraints['video'] = { ...constraints['video'], ...config.call.videoConstraints }
      console.log('videoConstraints are:', constraints['video'])
    }
    navigator.mediaDevices
    .getUserMedia(constraints)
    .then(replaceTrack)
    .catch(handleError);
  }

  const handleError = (error) => {
    console.error("Error: ", error);
  }

  const handleNudgeSend = useCallback((recipient) => {
    session.sendNudge(localConfbridgeChannelId, recipient, null)
    console.log('sending Nudge')
  }, [localConfbridgeChannelId, session])

  const handleKickSend = useCallback((recipient) => {
    session.sendAdminKick(localConfbridgeChannelId, recipient, null)
    console.log('sending Admin Kick')
  }, [localConfbridgeChannelId, session])
  
  return (
    <FullScreen handle={fullscreenHandle} className='live-call-call'>
      <div className='live-call-call'>
          {audioStreams.map((stream, i) => (
              <RTCAudio key={i} srcObject={stream} autoPlay></RTCAudio>
          ))}
          {/* {renderRemoteVideo()} */}
          {answered ? renderRemoteVideo() : renderOnHold()}
            <CallControls 
              muted={muted}
              videomuted={videomuted}
              screensharingAllowed={
                conferenceSettings 
                  ? 
                    conferenceSettings.enable_screensharing
                  : 
                    screensharingAllowed
              }
              screensharing={screensharingEnabled}
              cameraAllowed={cameraAllowed}
              hasDummyVideoTrack={hasDummyVideoTrack}
              chatEnabled={
                conferenceSettings
                  ?
                    conferenceSettings.enable_chat
                  :
                    true
              }
              fullscreen={fullscreen}
              dtmfEnabled={dtmfEnabled}
              audioDevices={audioDevices}
              videoDevices={videoDevices}
              selectedAudioDeviceId={localAudioInputId}
              selectedVideoDeviceId={localVideoInputId}
              confbridgeChannels={confbridgeChannels}
              confbridgeModerator={confbridgeModerator}
              localVideoStream={localVideoStream}
              dtmfActions={dtmfActions}
              drawerContent={drawerContent}
              conferenceSettings={conferenceSettings}
              setDrawerContent={(content) => {
                setDrawerContent(content)
                if (content === 'Conversation') setUnreadChatMessages(false)
              }}
              unreadChatMessages={unreadChatMessages}
              onEnd={() => {
                console.log('End Call pressed')
                session.terminate()
                onCallEnd()
              }}
              onMute={(state) => {
                console.log('Mute Pressed, change to state: ', state)
                state ? session.mute() : session.unmute() 
                setMuted(state)
              }}
              onVideoMute={(state) => {
                console.log('VideoMute Pressed, change to state: ', state)
                state ? session.videomute() : session.videounmute() 
                setVideomuted(state)
                if (!state && hasDummyVideoTrack) {
                  setHasDummyVideoTrack(false)
                  getCameraVideo(false, false)
                }
              }}
              onScreenshare={(state) => {
                console.log('Screenshare pressed, change to state: ', state)
                handleScreenshare(state)
              }}
              onFullscreen={(state) => {
                console.log('Fullscreen Pressed, change to state: ', state)
                state ? fullscreenHandle.enter() : fullscreenHandle.exit();
              }}
            />
      </div>
    </FullScreen>
  )
}